import _ from 'lodash';
import { HttpDataState, MsAnalyticsDataState, StateChangesState } from 'store/analytics/data/analyticsDataTypes';
import { reportsDataInitialState } from 'store/analytics/ui/subViews/reportingView/reportingViewReducer';
import { ChartSeries } from 'views/Analytics/AnalyticsMockService/Reporting/Report';
import { AnalyticsResponse, Metrics, MockServiceMetadata } from 'types/rest/analyticsTypes';
import { getGraphColor, getMetricDisplayText, getValueSuffix } from 'constants/chart-constants';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { PRINTABLE_REPORT_ID } from 'views/Analytics/AnalyticsMockService/Reporting/PrintableReport/PrintableReport';
import { generateDate } from 'utils/dateUtil';
import { MockServiceAnalytics, MsType } from 'types/rest/mockServiceTypes';

/** State for HTTP Call / Webhook tabs */
const initialHttpCallState: HttpDataState = {
  httpRequests: [],
  lastRefreshTs: null,
  isLoadingData: false,
  detail: null,
  isLoadingDetails: false,
  isOutdated: false,
  isDetailRequested: false,
};

/** State for State Changes tabs */
const initialStateChangesState: StateChangesState = {
  stateChangesTypes: [],
  lastRefreshTs: null,
  isLoadingData: false,
  detail: null,
  filters: null,
  changedRowsCount: 0,
  isLoadingDetails: false,
  isOutdated: false,
  isDetailRequested: false,
};

/**
 * Prepares initial empty data for Analytics data context.
 * @param tabId
 */
export const createInitialTabData = (tabId: string | number): MsAnalyticsDataState => ({
  tabId: tabId,
  sessionTracking: {},
  reporting: { ...reportsDataInitialState },
  inspection: {
    requests: [],
    requestsLoadedTs: null,
    isLoadingRequests: false,
    requestDetails: null,
    isLoadingRequestDetails: false,
    isRequestOutdated: false,
    isRequestSent: false,
  },
  webhook: { ...initialHttpCallState },
  httpCall: { ...initialHttpCallState },
  stateChanges: { ...initialStateChangesState },
});

export const saveWorkspace = (activeWorkspaceId: string) => {
  localStorage.setItem('activeWorkspaceId', activeWorkspaceId);
};

/**
 * Transforms raw data from backend to chart series which can be displayed by the HighCharts.
 * @param seriesData
 */
export const transformRawData = (rawData: AnalyticsResponse, msType: MsType): ChartSeries[] => {
  if (_.isNil(rawData) || (rawData.metrics && rawData.metrics.length === 0)) {
    return [];
  }

  const series: ChartSeries[] = _.chain(rawData.metrics)
    .sortBy((item: Metrics) => new Date(item.timestamp).getTime())
    .groupBy('metricType')
    .map((items: Metrics[], key) => {
      const palette = getGraphColor(key);
      return {
        name: getMetricDisplayText(key, msType),
        color: palette.color,
        borderColor: palette.border,
        data: items.map((metric: any) => {
          return [new Date(metric.timestamp).getTime(), metric.value];
        }),
        tooltip: {
          valueSuffix: getValueSuffix(key),
        },
      } as ChartSeries;
    })
    .value();

  return series;
};

/**
 * It processes PrintableReport component, creates canvas out of it, converts it to PNG format and saves it.
 */
export const downloadPngReport = async (reportName: string) => {
  try {
    // @ts-ignore
    const canvas = await html2canvas(document.querySelector(`#${PRINTABLE_REPORT_ID}`));
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.setAttribute('download', generateReportName(reportName));
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  } catch (error) {
    console.log(error);
  }
};

/**
 * It processes view component PrintableReport, creates canvas out of it, converts it to PNG format
 * and then it adds the image into the PDF and saves it with jsPDF.
 */
export const downloadPdfReport = async (reportName: string) => {
  try {
    // @ts-ignore
    const canvas = await html2canvas(document.querySelector(`#${PRINTABLE_REPORT_ID}`));
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'landscape',
    });

    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    //Slow compression (without it the file is too big)
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'SLOW');
    pdf.save(generateReportName(reportName) + '.pdf');
  } catch (error) {
    console.log(error);
  }
};

const generateReportName = (reportName: string) => {
  return reportName + '_' + generateDate();
};

/**
 * Gets virtual service list from the metadata wrapper.
 */
export const transformMockServiceAnalyticsFromMeta = (mockServiceMeta: MockServiceMetadata) => {
  return mockServiceMeta.analyticsMetaData.map((item: any) => {
    return {
      id: item.serviceMockId,
      name: item.name,
      meta: item,
      type: item.type,
      deleted: item.deleted,
    } as MockServiceAnalytics;
  });
};
